import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { ProjectFull } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { useCan } from "../../auth/roles";
import { OrgLayout } from "../../components/Shell";
import { UnsavedGuard } from "../../components/UnsavedChanges";
import { BusyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { rememberDeletion } from "../../features/org/deletions";
import { SiteList } from "./SdkKeysPage";

function sitesOf(app: ProjectFull): string[] {
  return (app.allowedOrigins ?? []).filter((site) => site && site !== "*");
}

function sameSites(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((site, i) => site === b[i]);
}

/** Apps belong to the organization. Keys and sessions belong to an app. */
export function AppsPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const can = useCan();
  const toast = useToast();
  const qc = useQueryClient();
  const apps = useQuery({ queryKey: queryKeys.projects(orgId), queryFn: () => api.orgs.projects(orgId) });
  const deletions = useQuery({
    queryKey: queryKeys.deletions(orgId),
    queryFn: () => api.orgs.deletions(orgId),
    enabled: can.admin,
    refetchInterval: (query) => (query.state.data?.some((d) => d.status === "running") ? 1000 : false),
  });
  const removing = new Set((deletions.data ?? []).filter((d) => d.kind === "app" && d.status === "running").map((d) => d.targetId));

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [sites, setSites] = useState<string[]>([]);
  const [sitePending, setSitePending] = useState(false);
  const [editing, setEditing] = useState<ProjectFull | null>(null);
  const [editName, setEditName] = useState("");
  const [editSites, setEditSites] = useState<string[]>([]);
  const [editPending, setEditPending] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [pending, setPending] = useState<ProjectFull | null>(null);

  const savedSites = editing ? sitesOf(editing) : [];
  const dirty = !!editing && (editName.trim() !== editing.name || !sameSites(editSites, savedSites));

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.projects(orgId) });
    void qc.invalidateQueries({ queryKey: queryKeys.projectPicker(orgId) });
  };
  const create = useMutation({
    mutationFn: () => api.orgs.createProject(orgId, { name: name.trim(), ...(sites.length ? { allowedOrigins: sites } : {}) }),
    onSuccess: () => {
      setOpen(false);
      setName("");
      setSites([]);
      refresh();
      toast.push("info", "App created.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const save = useMutation({
    mutationFn: () => api.projects.patch(editing!.id, { name: editName.trim(), allowedOrigins: editSites }),
    onSuccess: () => {
      setEditing(null);
      refresh();
      toast.push("info", "App saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (app: ProjectFull) => api.projects.revoke(app.id),
    onSuccess: (d) => {
      setPending(null);
      rememberDeletion(qc, orgId, d);
      refresh();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const rows = [...(apps.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const askClose = () => {
    if (save.isPending) return;
    if (dirty) setDiscard(true);
    else setEditing(null);
  };

  return (
    <OrgLayout orgId={orgId}>
      <UnsavedGuard dirty={dirty} />
      <PageHeader
        title="Apps"
        subtitle="Each app has its own keys and sessions."
        actions={can.admin ? <button type="button" className="btn primary" onClick={() => { setName(""); setSites([]); setOpen(true); }}>New app</button> : undefined}
      />
      <div className="card table-scroll stack-table">
      <DataTable<ProjectFull>
        ariaLabel="Apps"
        rows={rows}
        rowKey={(app) => app.id}
        loading={apps.isPending}
        empty="No apps yet."
        columns={[
          { key: "name", header: "Name", label: "Name", render: (app) => <strong>{app.name}</strong> },
          { key: "created", header: "Created", label: "Created", width: 160, render: (app) => formatRelative(app.createdAt) },
          {
            key: "actions",
            header: "",
            className: "row-actions",
            width: 44,
            align: "right",
            render: (app) => {
              if (app.deleting || removing.has(app.id)) return <span className="badge">Deleting…</span>;
              if (!can.admin) return null;
              return (
                <RowMenu label={`Actions for ${app.name}`}>
                  <button type="button" role="menuitem" onClick={() => { setEditing(app); setEditName(app.name); setEditSites(sitesOf(app)); }}>Edit</button>
                  <button type="button" role="menuitem" className="danger" onClick={() => setPending(app)}>Delete</button>
                </RowMenu>
              );
            },
          },
        ]}
      />
      </div>

      <Dialog
        open={open}
        onClose={() => { if (!create.isPending) setOpen(false); }}
        title="New app"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setOpen(false)} disabled={create.isPending}>Cancel</button>
            <BusyButton className="btn primary" busy={create.isPending} busyLabel="Creating…" disabled={!name.trim() || sitePending} onClick={() => create.mutate()}>Create</BusyButton>
          </>
        }
      >
        <Field label="App name" hint="How this app appears in session lists.">
          {(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />}
        </Field>
        <SiteList sites={sites} onChange={setSites} onPending={setSitePending} />
      </Dialog>

      <Dialog
        open={!!editing}
        onClose={askClose}
        title="Edit app"
        footer={
          <>
            <button type="button" className="btn" onClick={askClose} disabled={save.isPending}>Cancel</button>
            <BusyButton className="btn primary" busy={save.isPending} busyLabel="Saving…" disabled={!dirty || !editName.trim() || editPending} onClick={() => save.mutate()}>Save</BusyButton>
          </>
        }
      >
        <Field label="App name">
          {(p) => <input {...p} className="input" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />}
        </Field>
        <SiteList sites={editSites} onChange={setEditSites} onPending={setEditPending} />
      </Dialog>
      <ConfirmDialog
        open={discard}
        title="Discard changes"
        confirmLabel="Discard"
        onClose={() => setDiscard(false)}
        onConfirm={() => { setDiscard(false); setEditing(null); }}
      >
        <p style={{ margin: 0 }}>Unsaved changes to this app are discarded.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!pending}
        danger
        title="Delete app"
        confirmLabel="Delete app"
        busy={remove.isPending}
        onClose={() => { if (!remove.isPending) setPending(null); }}
        onConfirm={() => { if (pending && !remove.isPending) remove.mutate(pending); }}
      >
        <p style={{ margin: 0 }}>{pending ? `${pending.name} is deleted, including its keys and every session recorded for it.` : ""}</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
